// Reine Helfer: Kontakt-Vorbefüllung aus einer E-Mail (Anzeigename, From-Header,
// Signatur des Absenders). Quellen in dieser Reihenfolge:
//   1. emails.von_name  — der Anzeigename, den Outlook mitliefert (ab Sync-Version 2026-08-06)
//   2. der Anzeigename im From-Header ("Name <mail>"), falls doch mal einer drinsteht
//   3. die Signatur der Mail ("Mit freundlichen Grüßen" + folgende Zeile) — trägt den
//      Altbestand, bei dem der Anzeigename nie gespeichert wurde.
export interface KontaktPrefill {
  vorname: string;
  nachname: string;
  /** Gesetzt, wenn der Name nach einer Firma aussieht — dann öffnet das Formular den Unternehmen-Tab. */
  firmenname?: string;
  email: string;
  festnetz?: string;
  handynummer?: string;
}

// ── Namen ────────────────────────────────────────────────────────────────────

// Rechtsformen case-sensitiv (AG/KG/SE sind sonst zu kurz und träfen echte Namen),
// sprechende Bestandteile case-insensitiv.
const RECHTSFORM_RE = /(\b(GmbH|mbH|AG|KG|OHG|UG|SE|GbR|eG)\b|\be\.\s?[KV]\.)/;
const FIRMA_WORT_RE = /(&\s?co|\bltd\b|\binc\b|\bgesellschaft\b|\bgenossenschaft\b|\bhausverwaltung\b|\bimmobilien\b|\bverwaltung\b|\bsparkasse\b|\bvolksbank\b|\bbank\b|\bversicherung\b|\bstadtwerke\b|\bkanzlei\b|\bservice\b|\bgmbh\b)/i;

export function istFirmenname(name: string): boolean {
  const s = (name || '').trim();
  if (!s) return false;
  return RECHTSFORM_RE.test(s) || FIRMA_WORT_RE.test(s);
}

function teileNamePerson(name: string): { vorname: string; nachname: string } {
  const s = (name || '').trim();
  if (!s) return { vorname: '', nachname: '' };
  if (s.includes(',')) {
    const [nn, vn] = s.split(',');
    return { vorname: (vn || '').trim(), nachname: (nn || '').trim() };
  }
  const teile = s.split(/\s+/);
  if (teile.length === 1) return { vorname: '', nachname: teile[0] };
  return { vorname: teile.slice(0, -1).join(' '), nachname: teile[teile.length - 1] };
}

/** Namenszeile in Formularfelder zerlegen — Firmen landen in firmenname, nicht in Vor-/Nachname. */
export function teileName(name: string): { vorname: string; nachname: string; firmenname?: string } {
  const s = (name || '').trim();
  if (!s) return { vorname: '', nachname: '' };
  if (istFirmenname(s)) return { vorname: '', nachname: '', firmenname: s };
  return teileNamePerson(s);
}

function zerlegeVon(von: string): { name: string; email: string } {
  const s = (von || '').trim();
  const m = s.match(/<([^>]+)>/);
  const email = (m ? m[1] : (s.includes('@') ? s : '')).trim().toLowerCase();
  const name = (m ? s.slice(0, m.index).trim() : (s.includes('@') ? '' : s)).replace(/^["']|["']$/g, '').trim();
  return { name, email };
}

// From-Header parsen: "Name <mail>" oder nur "mail". Anzeigename → Vor-/Nachname (best-effort);
// "Nachname, Vorname" wird erkannt.
export function parseAbsender(von: string): { vorname: string; nachname: string; email: string } {
  const { name, email } = zerlegeVon(von);
  return { ...teileNamePerson(name), email };
}

// ── Zitat abschneiden ────────────────────────────────────────────────────────

const ZITAT_START = [
  /^-{2,}\s*(urspr[üu]ngliche nachricht|original message|weitergeleitete nachricht|forwarded message)/i,
  // web.de & Co. stellen dem Zitat einen Kopf voran, der mit „Gesendet:" beginnt.
  /^(von|from|gesendet|sent|gesendet von):\s/i,
  /^am\s.+\s(schrieb|wrote)\b/i,
  /^on\s.+\swrote:?\s*$/i,
  /^>/,
];

/**
 * Schneidet den zitierten Teil ab. Ohne das würden Signatur und Telefonnummer aus
 * einer zitierten Mail gelesen — bei einer Antwort auf uns wäre das unsere eigene.
 */
export function ohneZitat(text: string): string {
  const zeilen = (text || '').split(/\r?\n/);
  for (let i = 0; i < zeilen.length; i++) {
    const z = zeilen[i].trim();
    if (z && ZITAT_START.some((re) => re.test(z))) return zeilen.slice(0, i).join('\n').trimEnd();
  }
  return text || '';
}

// ── Telefon ──────────────────────────────────────────────────────────────────

const PHONE_RE = /(?:\+49|0)[\d\s/().-]{6,}\d/;
const PHONE_GLOBAL_RE = new RegExp(PHONE_RE, 'g');
const LABEL_RE = /(tel|telefon|fon|mobil|handy|phone)/i;
// Zeilen, in denen eine lange Ziffernfolge fast nie eine Telefonnummer ist.
const SPERR_RE = /(iban|bic|kunden|vertrag|rechnung|aktenzeichen|steuer|ust|hrb|amtsgericht|fax|bestell)/i;

function plausibel(s: string): boolean {
  const clean = s.trim();
  if (/^\d{1,2}[.]\d{1,2}[.]\d{2,4}$/.test(clean)) return false; // Datum ausschließen
  const n = clean.replace(/\D/g, '').length;
  return n >= 7 && n <= 15;
}

function istHandy(nummer: string): boolean {
  const ziffern = nummer.replace(/\D/g, '');
  const normal = ziffern.startsWith('49') ? '0' + ziffern.slice(2) : ziffern;
  return /^01[567]/.test(normal);
}

/**
 * Telefonnummern aus dem Text; Zeilen mit Telefon-Label haben Vorrang, sonst wird der
 * ganze Text durchsucht (ohne Zeilen mit IBAN/Kundennummer/Fax & Co.).
 * Klassifikation: 015x/016x/017x → Handy, sonst Festnetz. Es werden höchstens je eine
 * Festnetz- und eine Handynummer zurückgegeben.
 */
export function extrahiereTelefon(text: string): { festnetz?: string; handynummer?: string } {
  if (!text) return {};
  const zeilen = text.split(/\r?\n/);
  const gefunden: string[] = [];

  for (const zeile of zeilen) {
    if (!LABEL_RE.test(zeile)) continue;
    const m = zeile.match(PHONE_RE);
    if (m && plausibel(m[0])) gefunden.push(m[0].trim());
  }
  if (gefunden.length === 0) {
    for (const zeile of zeilen) {
      if (SPERR_RE.test(zeile)) continue;
      for (const treffer of zeile.match(PHONE_GLOBAL_RE) || []) {
        if (plausibel(treffer)) gefunden.push(treffer.trim());
      }
    }
  }

  const ergebnis: { festnetz?: string; handynummer?: string } = {};
  for (const roh of gefunden) {
    const nummer = roh.replace(/\s{2,}/g, ' ');
    if (istHandy(nummer)) { if (!ergebnis.handynummer) ergebnis.handynummer = nummer; }
    else if (!ergebnis.festnetz) ergebnis.festnetz = nummer;
    if (ergebnis.festnetz && ergebnis.handynummer) break;
  }
  return ergebnis;
}

// ── Name aus der Signatur ────────────────────────────────────────────────────

// „Grüsse" (Schweiz) genauso wie „Grüße"; „Grüßen" deckt der Wortstamm mit ab.
const GRUSS_RE = /^((mit )?(freundlichen?|besten?|vielen?|liebe[nr]?|herzlichen?|sch[öo]ne[nr]?)\s+gr[üu](ß|ss)|gr[üu](ß|ss)e|mfg|vg|lg|best regards|kind regards)/i;
const TITEL_RE = /^(i\.\s?[avAV]\.|ppa\.|dr\.|prof\.|dipl\.[-\wäöüß.]*|b\.\s?sc\.|m\.\s?sc\.|m\.\s?a\.)\s+/i;
// Zeilen, die zwar nach der Grußformel stehen, aber sicher kein Name sind.
const KEIN_NAME_RE = /(@|https?:|www\.|\bstr\.|\bstraße\b|\btel\b|\btelefon\b|\bmobil\b|\bfax\b|\bmail\b|\bgesendet\b|\bvon meinem\b|\bdiese e-mail\b)/i;

function istGrussformel(zeile: string): boolean {
  const z = zeile.replace(/[,.!;:\s]+$/, '').trim();
  return !!z && z.length <= 40 && GRUSS_RE.test(z);
}

function namensKandidat(zeile: string): string | null {
  // Klammerzusätze wie „(Schwiegersohn)" gehören nicht in den Nachnamen.
  const z = zeile
    .replace(/\([^)]*\)/g, ' ')
    .replace(/^[-–—*•\s]+/, '').replace(/[,;]+$/, '').trim()
    .replace(TITEL_RE, '').replace(/\s{2,}/g, ' ').trim();
  if (!z || z.length > 45) return null;
  if (KEIN_NAME_RE.test(z) || /\d/.test(z)) return null;
  if (z.split(/\s+/).length > 4) return null;
  if (!/[A-Za-zÀ-ÿ]/.test(z)) return null;
  return z;
}

/**
 * Name aus der Signatur: erste brauchbare Zeile nach der Grußformel. Deckt den Altbestand
 * ab, in dem der Anzeigename des Absenders nie gespeichert wurde.
 */
export function nameAusSignatur(text: string): { vorname: string; nachname: string; firmenname?: string } | null {
  const zeilen = (text || '').split(/\r?\n/);
  for (let i = 0; i < zeilen.length; i++) {
    if (!istGrussformel(zeilen[i])) continue;
    for (let j = i + 1; j < Math.min(i + 6, zeilen.length); j++) {
      const kandidat = namensKandidat(zeilen[j]);
      if (kandidat) return teileName(kandidat);
    }
  }
  return null;
}

// ── Name aus der Adresse ─────────────────────────────────────────────────────

const ROLLEN_ADRESSE = /^(info|kontakt|service|office|mail|email|buchhaltung|rechnung|team|support|noreply|no-reply|verwaltung|zentrale|post|hallo|anfrage|vertrieb)$/i;

/**
 * Letzter Ausweg: "vorname.nachname@…" → Vorname/Nachname. Nur bei einem klaren Trenner
 * zwischen zwei Wortteilen, nie bei Rollenadressen wie info@ — dort wäre jeder Rateversuch falsch.
 * Umlaute bleiben verschliffen (ruehl → Ruehl); besser als ein leeres Feld, aber korrekturbedürftig.
 */
export function nameAusEmail(email: string): { vorname: string; nachname: string } | null {
  const lokal = (email || '').split('@')[0] || '';
  if (!lokal) return null;
  const teile = lokal.split(/[._-]+/).filter(Boolean);
  if (teile.length !== 2) return null;
  if (!teile.every((t) => /^[a-zà-ÿ]{2,}$/i.test(t))) return null;
  if (teile.some((t) => ROLLEN_ADRESSE.test(t))) return null;
  const gross = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  return { vorname: gross(teile[0]), nachname: gross(teile[1]) };
}

// ── Vorbelegung ──────────────────────────────────────────────────────────────

export function baueKontaktPrefill(
  von: string,
  vonName: string | null | undefined,
  text: string | null,
): KontaktPrefill {
  const { name, email } = zerlegeVon(von);
  const eigenerText = ohneZitat(text || '');
  const roh = (vonName || '').trim() || name;
  // Manche Absender tragen als Anzeigename schlicht ihre Adresse ein — die steht schon im E-Mail-Feld.
  const anzeige = roh && !roh.includes('@') ? roh : '';
  const gewaehlt: { vorname: string; nachname: string; firmenname?: string } | null =
    (anzeige ? teileName(anzeige) : null) || nameAusSignatur(eigenerText) || nameAusEmail(email);
  const tel = extrahiereTelefon(eigenerText);
  return {
    vorname: gewaehlt?.vorname || '',
    nachname: gewaehlt?.nachname || '',
    ...(gewaehlt?.firmenname ? { firmenname: gewaehlt.firmenname } : {}),
    email,
    ...tel,
  };
}
